import type { GroupBy } from '../common/types.js';
import { evalFilter } from '../filter/eval.js';
import { filterMatcher } from '../filter/filter-fn/matcher.js';
import type { Filter, FilterGroup } from '../filter/types.js';
import type { Sort } from '../sort/types.js';

export type DatabaseProjectionRowScope =
  | 'filtered-result'
  | 'selected-cells'
  | 'temporal-window'
  | 'visible-window'
  | 'whole-database';

export type DatabaseProjectionResultSource =
  | 'index'
  | 'local-shards'
  | 'local-window'
  | 'materialized'
  | 'remote-scan';

export type DatabaseProjectionFreshness = 'live' | 'snapshot' | 'stale';

export type DatabaseProjectionSyncState =
  | 'live'
  | 'partial'
  | 'stale'
  | 'syncing'
  | 'unsupported';

export type DatabaseProjectionWindow = {
  limit: number;
  offset?: number;
};

export type DatabaseProjectionTextPredicate = {
  propertyIds?: readonly string[];
  query: string;
  type: 'text-search';
};

export type DatabaseProjectionColumnPredicate = {
  operator:
    | 'boolean-is'
    | 'contains'
    | 'equals'
    | 'is-empty'
    | 'is-not-empty'
    | 'not-equals';
  propertyId: string;
  type: 'column';
  value?: unknown;
};

export type DatabaseProjectionTemporalOverlapPredicate = {
  endPropertyId?: string;
  startPropertyId: string;
  type: 'temporal-overlap';
  windowEnd: Date | number | string;
  windowStart: Date | number | string;
};

export type DatabaseProjectionPredicateGroup = {
  operator: 'and' | 'or';
  predicates: readonly DatabaseProjectionPredicate[];
  type: 'predicate-group';
};

export type DatabaseProjectionPredicate =
  | DatabaseProjectionColumnPredicate
  | DatabaseProjectionPredicateGroup
  | DatabaseProjectionTemporalOverlapPredicate
  | DatabaseProjectionTextPredicate;

export type DatabaseProjectionSort = {
  direction?: 'asc' | 'desc';
  propertyId: string;
  type: 'property';
};

export type DatabaseProjectionAggregateType =
  | 'avg'
  | 'count'
  | 'max'
  | 'min'
  | 'sum';

export type DatabaseProjectionAggregateRequest = {
  id: string;
  propertyId?: string;
  type: DatabaseProjectionAggregateType;
};

export type DatabaseProjectionAggregateResult = {
  complete: boolean;
  id: string;
  type: DatabaseProjectionAggregateType;
  value: number | null;
};

export type DatabaseProjectionQuery = {
  aggregates?: readonly DatabaseProjectionAggregateRequest[];
  /**
   * View-level filter intent. Local SingleView execution evaluates this via the
   * view's existing row mapping; remote/index executors can translate the same
   * descriptor without views learning about storage details.
   */
  filter?: FilterGroup;
  groupBy?: GroupBy | readonly GroupBy[];
  predicates?: readonly DatabaseProjectionPredicate[];
  requestedPropertyIds?: readonly string[];
  scope: DatabaseProjectionRowScope;
  searchText?: string;
  sort?: readonly DatabaseProjectionSort[];
  viewSort?: Sort;
  viewId?: string;
  viewMode?: string;
  window?: DatabaseProjectionWindow;
};

export type DatabaseProjectionDiagnostic = {
  code:
    | 'invalid-date'
    | 'invalid-window'
    | 'partial-source'
    | 'unsupported-aggregate'
    | 'unsupported-operator'
    | 'unsupported-predicate'
    | 'unsupported-sort';
  message: string;
  path?: string;
};

export type DatabaseProjectionResult = {
  aggregates: readonly DatabaseProjectionAggregateResult[];
  complete: boolean;
  diagnostics: readonly DatabaseProjectionDiagnostic[];
  freshness: DatabaseProjectionFreshness;
  query: DatabaseProjectionQuery;
  rowIds: readonly string[];
  source: DatabaseProjectionResultSource;
  syncState: DatabaseProjectionSyncState;
  totalCount: number;
  totalKnown: boolean;
  window: {
    limit: number;
    offset: number;
  };
};

export type DatabaseProjectionSource = Pick<
  DatabaseProjectionResult,
  'complete' | 'diagnostics' | 'freshness' | 'rowIds' | 'source' | 'syncState'
>;

export type DatabaseProjectionExecutionInput = {
  complete: boolean;
  freshness: DatabaseProjectionFreshness;
  propertyIds: readonly string[];
  query: DatabaseProjectionQuery;
  readCellValue: (rowId: string, propertyId: string) => unknown;
  rowIds: readonly string[];
  rowPredicate?: (rowId: string) => boolean;
  source: DatabaseProjectionResultSource;
  syncState?: DatabaseProjectionSyncState;
};

function compareProjectionValues(left: unknown, right: unknown): number {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  const leftTime = parseProjectionDate(left);
  const rightTime = parseProjectionDate(right);
  if (leftTime !== undefined && rightTime !== undefined) {
    return leftTime - rightTime;
  }
  return String(left ?? '').localeCompare(String(right ?? ''));
}

function isEmptyProjectionValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function normalizeProjectionWindow(
  window: DatabaseProjectionWindow | undefined,
  totalCount: number,
  diagnostics: DatabaseProjectionDiagnostic[]
): { limit: number; offset: number } {
  const offset = window?.offset ?? 0;
  const limit = window?.limit ?? totalCount;
  if (!Number.isInteger(offset) || offset < 0) {
    diagnostics.push({
      code: 'invalid-window',
      message: 'Projection window offset must be a non-negative integer.',
      path: 'window.offset',
    });
    return { limit: 0, offset: 0 };
  }
  if (!Number.isInteger(limit) || limit < 0) {
    diagnostics.push({
      code: 'invalid-window',
      message: 'Projection window limit must be a non-negative integer.',
      path: 'window.limit',
    });
    return { limit: 0, offset: 0 };
  }
  return { limit, offset };
}

function parseProjectionDate(value: unknown): number | undefined {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim()) {
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : undefined;
  }
  return undefined;
}

function projectionCellText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function collectUnsupportedFilterDiagnostics(
  filter: Filter | undefined,
  diagnostics: DatabaseProjectionDiagnostic[],
  path = 'filter'
): boolean {
  if (!filter) return true;
  if (filter.type === 'group') {
    return filter.conditions.every((condition, index) =>
      collectUnsupportedFilterDiagnostics(
        condition,
        diagnostics,
        `${path}.conditions.${index}`
      )
    );
  }
  if (!filterMatcher.getFilterByName(filter.function)) {
    diagnostics.push({
      code: 'unsupported-operator',
      message: `Unsupported projection filter operator "${filter.function ?? ''}".`,
      path: `${path}.function`,
    });
    return false;
  }
  return true;
}

function rowMatchesFilter({
  filter,
  propertyIds,
  readCellValue,
  rowId,
}: {
  filter: FilterGroup;
  propertyIds: readonly string[];
  readCellValue: DatabaseProjectionExecutionInput['readCellValue'];
  rowId: string;
}): boolean {
  if (!filter.conditions.length) return true;
  const row = Object.fromEntries(
    propertyIds.map(propertyId => [
      propertyId,
      readCellValue(rowId, propertyId),
    ])
  );
  return evalFilter(filter, row);
}

function rowMatchesColumnPredicate({
  predicate,
  readCellValue,
  rowId,
}: {
  predicate: DatabaseProjectionColumnPredicate;
  readCellValue: DatabaseProjectionExecutionInput['readCellValue'];
  rowId: string;
}): boolean {
  const value = readCellValue(rowId, predicate.propertyId);
  switch (predicate.operator) {
    case 'boolean-is':
      return Boolean(value) === Boolean(predicate.value);
    case 'contains':
      return projectionCellText(value)
        .toLowerCase()
        .includes(String(predicate.value ?? '').toLowerCase());
    case 'equals':
      return Object.is(value, predicate.value);
    case 'is-empty':
      return isEmptyProjectionValue(value);
    case 'is-not-empty':
      return !isEmptyProjectionValue(value);
    case 'not-equals':
      return !Object.is(value, predicate.value);
  }
}

function rowMatchesPredicate({
  diagnostics,
  predicate,
  propertyIds,
  readCellValue,
  rowId,
}: {
  diagnostics: DatabaseProjectionDiagnostic[];
  predicate: DatabaseProjectionPredicate;
  propertyIds: readonly string[];
  readCellValue: DatabaseProjectionExecutionInput['readCellValue'];
  rowId: string;
}): boolean {
  switch (predicate.type) {
    case 'column':
      return rowMatchesColumnPredicate({ predicate, readCellValue, rowId });
    case 'predicate-group':
      return predicate.operator === 'or'
        ? predicate.predicates.some(child =>
            rowMatchesPredicate({
              diagnostics,
              predicate: child,
              propertyIds,
              readCellValue,
              rowId,
            })
          )
        : predicate.predicates.every(child =>
            rowMatchesPredicate({
              diagnostics,
              predicate: child,
              propertyIds,
              readCellValue,
              rowId,
            })
          );
    case 'temporal-overlap': {
      const windowStart = parseProjectionDate(predicate.windowStart);
      const windowEnd = parseProjectionDate(predicate.windowEnd);
      if (windowStart === undefined || windowEnd === undefined) {
        diagnostics.push({
          code: 'invalid-date',
          message: 'Temporal projection requires a valid time window.',
          path: 'predicates.temporal-overlap',
        });
        return false;
      }
      const start = parseProjectionDate(
        readCellValue(rowId, predicate.startPropertyId)
      );
      const end = parseProjectionDate(
        predicate.endPropertyId
          ? readCellValue(rowId, predicate.endPropertyId)
          : undefined
      );
      if (start === undefined) {
        return false;
      }
      const normalizedEnd = Math.max(start, end ?? start);
      return start < windowEnd && normalizedEnd > windowStart;
    }
    case 'text-search': {
      const needle = predicate.query.trim().toLowerCase();
      if (!needle) return true;
      const searchableProperties = predicate.propertyIds?.length
        ? predicate.propertyIds
        : propertyIds;
      return searchableProperties.some(propertyId =>
        projectionCellText(readCellValue(rowId, propertyId))
          .toLowerCase()
          .includes(needle)
      );
    }
  }
}

function calculateProjectionAggregate({
  complete,
  filteredRowIds,
  readCellValue,
  request,
}: {
  complete: boolean;
  filteredRowIds: readonly string[];
  readCellValue: DatabaseProjectionExecutionInput['readCellValue'];
  request: DatabaseProjectionAggregateRequest;
}): DatabaseProjectionAggregateResult {
  if (request.type === 'count') {
    return {
      complete,
      id: request.id,
      type: request.type,
      value: filteredRowIds.length,
    };
  }
  if (!request.propertyId) {
    return {
      complete: false,
      id: request.id,
      type: request.type,
      value: null,
    };
  }
  const values = filteredRowIds
    .map(rowId => Number(readCellValue(rowId, request.propertyId!)))
    .filter(Number.isFinite);
  if (!values.length) {
    return {
      complete,
      id: request.id,
      type: request.type,
      value: null,
    };
  }
  switch (request.type) {
    case 'avg':
      return {
        complete,
        id: request.id,
        type: request.type,
        value: values.reduce((sum, value) => sum + value, 0) / values.length,
      };
    case 'max':
      return {
        complete,
        id: request.id,
        type: request.type,
        value: Math.max(...values),
      };
    case 'min':
      return {
        complete,
        id: request.id,
        type: request.type,
        value: Math.min(...values),
      };
    case 'sum':
      return {
        complete,
        id: request.id,
        type: request.type,
        value: values.reduce((sum, value) => sum + value, 0),
      };
  }
}

function collectUnsupportedAggregateDiagnostics(
  query: DatabaseProjectionQuery,
  diagnostics: DatabaseProjectionDiagnostic[]
): void {
  query.aggregates?.forEach((aggregate, index) => {
    if (aggregate.type === 'count' || aggregate.propertyId) {
      return;
    }
    diagnostics.push({
      code: 'unsupported-aggregate',
      message: `Projection aggregate "${aggregate.type}" requires a propertyId.`,
      path: `aggregates.${index}.propertyId`,
    });
  });
  if (query.groupBy && query.aggregates?.length) {
    diagnostics.push({
      code: 'unsupported-aggregate',
      message:
        'Grouped projection aggregates require a grouped aggregate executor.',
      path: 'groupBy',
    });
  }
}

function getProjectionSorts(
  query: DatabaseProjectionQuery
): DatabaseProjectionSort[] {
  return [
    ...(query.viewSort?.sortBy.map(sort => ({
      direction: sort.desc ? ('desc' as const) : ('asc' as const),
      propertyId: sort.ref.name,
      type: 'property' as const,
    })) ?? []),
    ...(query.sort ?? []),
  ];
}

function collectFilterPropertyIds(
  filter: Filter | undefined,
  ids: Set<string>
) {
  if (!filter) return;
  if (filter.type === 'group') {
    filter.conditions.forEach(condition =>
      collectFilterPropertyIds(condition, ids)
    );
    return;
  }
  if (filter.left.type === 'ref') {
    ids.add(filter.left.name);
  }
}

function collectPredicatePropertyIds(
  predicate: DatabaseProjectionPredicate,
  ids: Set<string>
) {
  if (predicate.type === 'column') {
    ids.add(predicate.propertyId);
  } else if (predicate.type === 'temporal-overlap') {
    ids.add(predicate.startPropertyId);
    if (predicate.endPropertyId) ids.add(predicate.endPropertyId);
  } else if (predicate.type === 'text-search') {
    predicate.propertyIds?.forEach(propertyId => ids.add(propertyId));
  } else {
    predicate.predicates.forEach(child =>
      collectPredicatePropertyIds(child, ids)
    );
  }
}

export function getProjectionPropertyIds(
  query: DatabaseProjectionQuery,
  allPropertyIds: readonly string[]
): readonly string[] {
  if (
    query.searchText?.trim() ||
    query.predicates?.some(
      predicate =>
        predicate.type === 'text-search' && !predicate.propertyIds?.length
    )
  ) {
    return allPropertyIds;
  }
  const ids = new Set(query.requestedPropertyIds ?? []);
  collectFilterPropertyIds(query.filter, ids);
  query.predicates?.forEach(predicate =>
    collectPredicatePropertyIds(predicate, ids)
  );
  getProjectionSorts(query).forEach(sort => ids.add(sort.propertyId));
  query.aggregates?.forEach(aggregate => {
    if (aggregate.propertyId) ids.add(aggregate.propertyId);
  });
  return ids.size ? [...ids] : allPropertyIds;
}

function sortByManualOrder(
  rowIds: readonly string[],
  manuallySort: readonly string[] | undefined
): string[] {
  if (!manuallySort?.length) return rowIds.slice();
  const manualIndex = new Map(
    manuallySort.map((rowId, index) => [rowId, index])
  );
  return rowIds.slice().sort((left, right) => {
    const leftIndex = manualIndex.get(left);
    const rightIndex = manualIndex.get(right);
    if (leftIndex === undefined && rightIndex === undefined) return 0;
    if (leftIndex === undefined) return 1;
    if (rightIndex === undefined) return -1;
    return leftIndex - rightIndex;
  });
}

export function canReuseProjectionSourceRows(
  query: DatabaseProjectionQuery,
  hasRowPredicate: boolean
): boolean {
  return !(
    hasRowPredicate ||
    query.aggregates?.length ||
    query.filter?.conditions.length ||
    query.predicates?.length ||
    query.searchText?.trim() ||
    query.sort?.length ||
    query.viewSort?.manuallySort.length ||
    query.viewSort?.sortBy.length ||
    query.window
  );
}

export function canProjectSourceWindow(
  query: DatabaseProjectionQuery,
  hasRowPredicate: boolean
): boolean {
  return Boolean(
    query.window &&
    canReuseProjectionSourceRows(
      { ...query, window: undefined },
      hasRowPredicate
    )
  );
}

export function projectDatabaseRows(
  input: DatabaseProjectionExecutionInput
): DatabaseProjectionResult {
  if (canReuseProjectionSourceRows(input.query, Boolean(input.rowPredicate))) {
    const diagnostics: DatabaseProjectionDiagnostic[] = input.complete
      ? []
      : [
          {
            code: 'partial-source',
            message:
              'Projection source is partial; result must not be rendered as complete.',
          },
        ];
    return {
      aggregates: [],
      complete: input.complete,
      diagnostics,
      freshness: input.freshness,
      query: input.query,
      rowIds: input.rowIds,
      source: input.source,
      syncState: input.syncState ?? (input.complete ? 'live' : 'partial'),
      totalCount: input.rowIds.length,
      totalKnown: input.complete,
      window: { limit: input.rowIds.length, offset: 0 },
    };
  }
  if (canProjectSourceWindow(input.query, Boolean(input.rowPredicate))) {
    const diagnostics: DatabaseProjectionDiagnostic[] = [];
    const window = normalizeProjectionWindow(
      input.query.window,
      input.rowIds.length,
      diagnostics
    );
    const valid = diagnostics.length === 0;
    if (!input.complete) {
      diagnostics.push({
        code: 'partial-source',
        message:
          'Projection source is partial; result must not be rendered as complete.',
      });
    }
    return {
      aggregates: [],
      complete: input.complete && valid,
      diagnostics,
      freshness: input.freshness,
      query: input.query,
      rowIds:
        window.limit === 0
          ? []
          : input.rowIds.slice(window.offset, window.offset + window.limit),
      source: input.source,
      syncState: valid
        ? (input.syncState ?? (input.complete ? 'live' : 'partial'))
        : 'unsupported',
      totalCount: input.rowIds.length,
      totalKnown: input.complete && valid,
      window,
    };
  }
  const diagnostics: DatabaseProjectionDiagnostic[] = [];
  const predicateDiagnostics = new Set<string>();
  const searchText = input.query.searchText?.trim();
  const predicates = [
    ...(searchText
      ? [{ query: searchText, type: 'text-search' } as const]
      : []),
    ...(input.query.predicates ?? []),
  ];
  const filterValid = collectUnsupportedFilterDiagnostics(
    input.query.filter,
    diagnostics
  );
  collectUnsupportedAggregateDiagnostics(input.query, diagnostics);
  let filteredRowIds = input.rowIds.filter(rowId => {
    if (input.rowPredicate && !input.rowPredicate(rowId)) {
      return false;
    }
    if (
      input.query.filter &&
      (!filterValid ||
        !rowMatchesFilter({
          filter: input.query.filter,
          propertyIds: input.propertyIds,
          readCellValue: input.readCellValue,
          rowId,
        }))
    ) {
      return false;
    }
    const previousDiagnosticCount = diagnostics.length;
    const matches = predicates.every(predicate =>
      rowMatchesPredicate({
        diagnostics,
        predicate,
        propertyIds: input.propertyIds,
        readCellValue: input.readCellValue,
        rowId,
      })
    );
    for (const diagnostic of diagnostics.slice(previousDiagnosticCount)) {
      const key = `${diagnostic.code}:${diagnostic.path ?? ''}`;
      if (predicateDiagnostics.has(key)) {
        diagnostics.pop();
      } else {
        predicateDiagnostics.add(key);
      }
    }
    return matches;
  });

  filteredRowIds = sortByManualOrder(
    filteredRowIds,
    input.query.viewSort?.manuallySort
  );

  for (const sort of getProjectionSorts(input.query)) {
    if (sort.type !== 'property') {
      diagnostics.push({
        code: 'unsupported-sort',
        message: `Unsupported projection sort "${String(sort.type)}".`,
        path: 'sort.type',
      });
      continue;
    }
    const direction = sort.direction === 'desc' ? -1 : 1;
    filteredRowIds = [...filteredRowIds].sort(
      (left, right) =>
        direction *
        compareProjectionValues(
          input.readCellValue(left, sort.propertyId),
          input.readCellValue(right, sort.propertyId)
        )
    );
  }

  const totalCount = filteredRowIds.length;
  const window = normalizeProjectionWindow(
    input.query.window,
    totalCount,
    diagnostics
  );
  const rowIds =
    window.limit === 0
      ? []
      : filteredRowIds.slice(window.offset, window.offset + window.limit);
  const valid = diagnostics.length === 0;
  const complete = input.complete && valid;
  const syncState: DatabaseProjectionSyncState = valid
    ? (input.syncState ?? (input.complete ? 'live' : 'partial'))
    : 'unsupported';

  if (!input.complete) {
    diagnostics.push({
      code: 'partial-source',
      message:
        'Projection source is partial; result must not be rendered as complete.',
    });
  }

  return {
    aggregates: (input.query.aggregates ?? []).map(request =>
      calculateProjectionAggregate({
        complete,
        filteredRowIds,
        readCellValue: input.readCellValue,
        request,
      })
    ),
    complete,
    diagnostics,
    freshness: input.freshness,
    query: input.query,
    rowIds,
    source: input.source,
    syncState,
    totalCount,
    totalKnown: complete,
    window,
  };
}
